/**
 * SWISS School Portal — "Jump to anything" palette
 * ================================================
 * Backs the search affordance in the top bar. The previous header
 * had a search box that did nothing, and it was removed for that
 * reason; the redesign puts search back on every screen, so this
 * gives it something real to do — fuzzy-ish filtering over the nav
 * model, keyboard-driven, navigating on select.
 *
 * Scope is deliberately the nav model only. It does not search
 * school records or report contents; the placeholder text promises
 * pages, not data.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { schoolHasModule } from '../services/tiers';
import './JumpTo.css';

/** Flatten sections into a single searchable list of destinations. */
function buildIndex(sections, school) {
  const out = [];
  for (const section of sections) {
    if (!section.items) {
      out.push({ path: section.to, label: section.label, section: null, locked: false });
      continue;
    }
    for (const item of section.items) {
      if (item.requiresAssessments && !school?.assessmentsEnabled) continue;
      out.push({
        path: item.path,
        label: item.label,
        section: section.label,
        locked: !!item.module && !schoolHasModule(school, item.module),
      });
    }
  }
  out.push({ path: '/settings', label: 'Settings', section: null, locked: false });
  return out;
}

const JumpTo = ({ open, sections, onClose }) => {
  const navigate = useNavigate();
  const { school } = useAuth();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const index = useMemo(() => buildIndex(sections, school), [sections, school]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return index;
    return index.filter((entry) => {
      const haystack = `${entry.label} ${entry.section || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [index, query]);

  // Reset each time it opens, and focus the field.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the cursor inside the (shrinking) result list as you type.
  useEffect(() => { setCursor(0); }, [query]);

  // Escape at the document level, not just on the input. The field
  // autofocuses so the input's own handler covers the common case, but
  // once focus moves off it — tabbing out, or clicking inside the panel
  // — Escape stopped dismissing and only the scrim worked. MobileDrawer
  // already listens this way; this keeps the two overlays consistent.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (entry) => {
    if (!entry) return;
    onClose();
    navigate(entry.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[cursor]);
    }
  };

  return (
    <>
      <div className="jumpto-scrim" onClick={onClose} aria-hidden="true" />
      <div className="jumpto" role="dialog" aria-modal="true" aria-label="Jump to a page">
        <div className="jumpto-field">
          <Search size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Jump to anything…"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search pages"
            aria-controls="jumpto-results"
          />
          <kbd aria-hidden="true">esc</kbd>
        </div>

        <ul className="jumpto-results" id="jumpto-results" role="listbox">
          {results.length === 0 && (
            <li className="jumpto-empty">No page matches “{query}”.</li>
          )}
          {results.map((entry, i) => (
            <li key={entry.path}>
              <button
                type="button"
                role="option"
                aria-selected={i === cursor}
                className={`jumpto-result ${i === cursor ? 'cursor' : ''}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(entry)}
              >
                <span className="jumpto-result-label">{entry.label}</span>
                {entry.locked && <Lock size={12} aria-hidden="true" />}
                {entry.section && <span className="jumpto-result-section">{entry.section}</span>}
                {i === cursor && <CornerDownLeft size={13} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default JumpTo;
